/**
 * BossHealthBar - Barra de vida del boss
 *
 * Se muestra en la parte superior de la pantalla cuando un boss se activa.
 * Usa REX UI para el layout y animaciones.
 */

import { Scene, GameObjects } from 'phaser';
import { t } from '../i18n/Localization';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { Boss } from '../entities/base/Boss';
import { LayerDepths } from '../constants/enums';

export interface BossHealthBarConfig {
  scene: Scene;
}

// Colors
const COLOR_BG = 0x1a1a2e;
const COLOR_BORDER = 0x4a4a6a;
const COLOR_HEALTH = 0xc62828;
const COLOR_HEALTH_LOW = 0xff5722;
const COLOR_TEXT = 0xffffff;

export class BossHealthBar extends GameObjects.Container {
  private rexUI: UIPlugin;
  private panel: GameObjects.Container;
  private bgGraphics!: GameObjects.Graphics;
  private healthBarBg: GameObjects.Graphics;
  private healthBarFill: GameObjects.Graphics;
  private healthBarShine: GameObjects.Graphics;
  private damageFlash: GameObjects.Graphics;
  private nameText: GameObjects.Text;
  private hpText: GameObjects.Text;
  private portraitSprite: GameObjects.Sprite | null = null;
  private portraitBorder: GameObjects.Graphics;
  private boss: Boss | null = null;
  private previousHp: number = 0;
  private lowHealthTween: Phaser.Tweens.Tween | null = null;
  private isLowHealthPulsing: boolean = false;

  private static readonly BAR_WIDTH = 350;
  private static readonly BAR_HEIGHT = 28;
  private static readonly BAR_RADIUS = 8;
  private static readonly PADDING = 20;
  private static readonly PORTRAIT_SIZE = 80;
  private static readonly PORTRAIT_OFFSET = 60;

  constructor(config: BossHealthBarConfig) {
    const { width } = config.scene.scale;
    super(config.scene, width / 2, 60);

    this.rexUI = (config.scene as Scene & { rexUI: UIPlugin }).rexUI;

    // Create panel container
    this.panel = config.scene.add.container(0, 0);
    this.add(this.panel);

    // Background panel with rounded corners (larger to fit portrait)
    const bgWidth = BossHealthBar.BAR_WIDTH + BossHealthBar.PORTRAIT_SIZE + BossHealthBar.PADDING * 3;
    const bgHeight = BossHealthBar.PORTRAIT_SIZE + BossHealthBar.PADDING;

    this.bgGraphics = config.scene.add.graphics();
    this.bgGraphics.fillStyle(COLOR_BG, 0.95);
    this.bgGraphics.fillRoundedRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, 12);
    this.bgGraphics.lineStyle(3, COLOR_BORDER, 1);
    this.bgGraphics.strokeRoundedRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, 12);
    this.panel.add(this.bgGraphics);

    // Portrait border (golden frame)
    const portraitX = -bgWidth / 2 + BossHealthBar.PADDING + BossHealthBar.PORTRAIT_SIZE / 2;
    this.portraitBorder = config.scene.add.graphics();
    this.portraitBorder.lineStyle(3, 0xffd700, 1);
    this.portraitBorder.strokeRoundedRect(
      portraitX - BossHealthBar.PORTRAIT_SIZE / 2 - 2,
      -BossHealthBar.PORTRAIT_SIZE / 2 - 2,
      BossHealthBar.PORTRAIT_SIZE + 4,
      BossHealthBar.PORTRAIT_SIZE + 4,
      8
    );
    this.panel.add(this.portraitBorder);

    // Content offset (to the right of portrait)
    const contentX = portraitX + BossHealthBar.PORTRAIT_SIZE / 2 + BossHealthBar.PADDING;
    const contentWidth = BossHealthBar.BAR_WIDTH;

    // Boss name text with shadow
    this.nameText = config.scene.add.text(contentX + contentWidth / 2, -20, '', {
      fontFamily: 'Arial',
      fontSize: '22px',
      color: '#ffffff',
      fontStyle: 'bold',
      shadow: {
        offsetX: 2,
        offsetY: 2,
        color: '#000000',
        blur: 4,
        fill: true,
      },
    });
    this.nameText.setOrigin(0.5, 0.5);
    this.panel.add(this.nameText);

    // Health bar background (rounded)
    this.healthBarBg = config.scene.add.graphics();
    this.drawBarBackground(contentX);
    this.panel.add(this.healthBarBg);

    // Health bar fill (gradient)
    this.healthBarFill = config.scene.add.graphics();
    this.panel.add(this.healthBarFill);

    // Health bar shine effect
    this.healthBarShine = config.scene.add.graphics();
    this.panel.add(this.healthBarShine);

    // Damage flash overlay
    this.damageFlash = config.scene.add.graphics();
    this.damageFlash.setAlpha(0);
    this.panel.add(this.damageFlash);

    // HP text (centered on bar)
    this.hpText = config.scene.add.text(contentX + contentWidth / 2, 18, '', {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold',
      shadow: {
        offsetX: 1,
        offsetY: 1,
        color: '#000000',
        blur: 3,
        fill: true,
      },
    });
    this.hpText.setOrigin(0.5, 0.5);
    this.panel.add(this.hpText);

    // Fixed to camera
    this.setScrollFactor(0);
    this.setDepth(LayerDepths.HUD);

    // Initially hidden
    this.setVisible(false);
    this.setAlpha(0);

    config.scene.add.existing(this);
  }

  /**
   * Get the content X offset (right of portrait)
   */
  private getContentX(): number {
    const bgWidth = BossHealthBar.BAR_WIDTH + BossHealthBar.PORTRAIT_SIZE + BossHealthBar.PADDING * 3;
    const portraitX = -bgWidth / 2 + BossHealthBar.PADDING + BossHealthBar.PORTRAIT_SIZE / 2;
    return portraitX + BossHealthBar.PORTRAIT_SIZE / 2 + BossHealthBar.PADDING;
  }

  /**
   * Draw the bar background with rounded corners
   */
  private drawBarBackground(contentX?: number): void {
    const cx = contentX ?? this.getContentX();
    const barX = cx;
    const barY = 18 - BossHealthBar.BAR_HEIGHT / 2;

    this.healthBarBg.clear();
    this.healthBarBg.fillStyle(0x222233, 1);
    this.healthBarBg.fillRoundedRect(
      barX, barY,
      BossHealthBar.BAR_WIDTH, BossHealthBar.BAR_HEIGHT,
      BossHealthBar.BAR_RADIUS
    );
    this.healthBarBg.lineStyle(2, COLOR_BORDER, 1);
    this.healthBarBg.strokeRoundedRect(
      barX, barY,
      BossHealthBar.BAR_WIDTH, BossHealthBar.BAR_HEIGHT,
      BossHealthBar.BAR_RADIUS
    );
  }

  /**
   * Draw the health bar fill with gradient effect
   */
  private drawHealthFill(percent: number, isLow: boolean): void {
    const cx = this.getContentX();
    const barX = cx + 3;
    const barY = 18 - BossHealthBar.BAR_HEIGHT / 2 + 3;
    const fillWidth = Math.max(0, (BossHealthBar.BAR_WIDTH - 6) * percent);
    const fillHeight = BossHealthBar.BAR_HEIGHT - 6;

    this.healthBarFill.clear();
    if (fillWidth <= 0) return;

    // Main color (gradient simulation with multiple rects)
    const baseColor = isLow ? COLOR_HEALTH_LOW : COLOR_HEALTH;
    const lightColor = isLow ? 0xff8a65 : 0xef5350;
    const darkColor = isLow ? 0xbf360c : 0x8e0000;

    // Draw gradient (dark at bottom, light at top)
    const steps = 3;
    const stepHeight = fillHeight / steps;

    // Bottom (darker)
    this.healthBarFill.fillStyle(darkColor, 1);
    this.healthBarFill.fillRoundedRect(barX, barY + stepHeight * 2, fillWidth, stepHeight, {
      tl: 0, tr: 0, bl: BossHealthBar.BAR_RADIUS - 2, br: BossHealthBar.BAR_RADIUS - 2
    });

    // Middle
    this.healthBarFill.fillStyle(baseColor, 1);
    this.healthBarFill.fillRoundedRect(barX, barY + stepHeight, fillWidth, stepHeight, 0);

    // Top (lighter)
    this.healthBarFill.fillStyle(lightColor, 1);
    this.healthBarFill.fillRoundedRect(barX, barY, fillWidth, stepHeight, {
      tl: BossHealthBar.BAR_RADIUS - 2, tr: BossHealthBar.BAR_RADIUS - 2, bl: 0, br: 0
    });

    // Shine effect at top
    this.healthBarShine.clear();
    this.healthBarShine.fillStyle(0xffffff, 0.3);
    this.healthBarShine.fillRoundedRect(barX + 2, barY + 2, fillWidth - 4, stepHeight - 2, {
      tl: BossHealthBar.BAR_RADIUS - 3, tr: BossHealthBar.BAR_RADIUS - 3, bl: 0, br: 0
    });
  }

  /**
   * Trigger damage flash effect
   */
  private triggerDamageFlash(): void {
    const cx = this.getContentX();
    const barX = cx;
    const barY = 18 - BossHealthBar.BAR_HEIGHT / 2;

    this.damageFlash.clear();
    this.damageFlash.fillStyle(0xffffff, 1);
    this.damageFlash.fillRoundedRect(
      barX, barY,
      BossHealthBar.BAR_WIDTH, BossHealthBar.BAR_HEIGHT,
      BossHealthBar.BAR_RADIUS
    );
    this.damageFlash.setAlpha(0.6);

    this.scene.tweens.add({
      targets: this.damageFlash,
      alpha: 0,
      duration: 150,
      ease: 'Cubic.easeOut',
    });
  }

  /**
   * Start low health pulse animation
   */
  private startLowHealthPulse(): void {
    if (this.isLowHealthPulsing) return;
    this.isLowHealthPulsing = true;

    this.lowHealthTween = this.scene.tweens.add({
      targets: this.healthBarFill,
      alpha: 0.6,
      duration: 300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Stop low health pulse animation
   */
  private stopLowHealthPulse(): void {
    if (!this.isLowHealthPulsing) return;
    this.isLowHealthPulsing = false;

    if (this.lowHealthTween) {
      this.lowHealthTween.stop();
      this.lowHealthTween = null;
    }
    this.healthBarFill.setAlpha(1);
  }

  /**
   * Show the boss health bar with animation
   */
  show(boss: Boss, name: string, portrait?: string, nameKey?: string): void {
    console.log(`[BossHealthBar] show() called - name: "${name}", portrait: "${portrait}", boss HP: ${boss.hp}/${boss.maxHp}`);
    this.boss = boss;
    this.previousHp = boss.hp;
    const displayName = nameKey ? t(nameKey) : name;
    this.nameText.setText(displayName);
    this.stopLowHealthPulse();

    // Set portrait if provided
    this.setPortrait(portrait);
    this.updateHealth();

    this.setVisible(true);

    // Slide in from top
    this.setY(-80);
    this.scene.tweens.add({
      targets: this,
      y: 60,
      alpha: 1,
      duration: 500,
      ease: 'Back.easeOut',
      onComplete: () => {
        console.log('[BossHealthBar] Show animation complete');
      },
    });
  }

  /**
   * Set the boss portrait
   */
  private setPortrait(portraitKey?: string): void {
    // Remove existing portrait
    if (this.portraitSprite) {
      this.portraitSprite.destroy();
      this.portraitSprite = null;
    }

    if (!portraitKey) return;

    // Check if texture exists
    if (!this.scene.textures.exists(portraitKey)) {
      console.warn(`[BossHealthBar] Portrait texture "${portraitKey}" not found`);
      return;
    }

    // Calculate portrait position
    const bgWidth = BossHealthBar.BAR_WIDTH + BossHealthBar.PORTRAIT_SIZE + BossHealthBar.PADDING * 3;
    const portraitX = -bgWidth / 2 + BossHealthBar.PADDING + BossHealthBar.PORTRAIT_SIZE / 2;

    // Get the first frame from the atlas
    const texture = this.scene.textures.get(portraitKey);
    const frameNames = texture.getFrameNames();
    const firstFrame = frameNames.length > 0 ? frameNames[0] : undefined;

    // Create portrait sprite with frame
    this.portraitSprite = this.scene.add.sprite(portraitX, 0, portraitKey, firstFrame);
    this.portraitSprite.setDisplaySize(BossHealthBar.PORTRAIT_SIZE - 4, BossHealthBar.PORTRAIT_SIZE - 4);
    this.panel.add(this.portraitSprite);

    console.log(`[BossHealthBar] Portrait set: "${portraitKey}", frame: "${firstFrame}"`);
  }

  /**
   * Hide the boss health bar with animation
   */
  hide(reason: string = 'unknown'): void {
    console.log(`[BossHealthBar] hide() called - reason: ${reason}`);
    this.stopLowHealthPulse();

    // Notify boss that health bar is hidden (so it can re-show when player returns)
    const bossRef = this.boss;
    if (bossRef && reason !== 'boss died') {
      bossRef.onHealthBarHidden();
    }

    this.scene.tweens.add({
      targets: this,
      y: -80,
      alpha: 0,
      duration: 300,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        console.log('[BossHealthBar] Hide animation complete');
        this.setVisible(false);
        this.boss = null;
        // Clean up portrait
        if (this.portraitSprite) {
          this.portraitSprite.destroy();
          this.portraitSprite = null;
        }
      },
    });
  }

  /**
   * Update health bar based on boss HP
   */
  updateHealth(): void {
    if (!this.boss) return;

    const currentHp = this.boss.hp;
    const maxHp = this.boss.maxHp;
    const healthPercent = Math.max(0, currentHp / maxHp);
    const isLow = healthPercent < 0.25;

    // Check for damage taken
    if (currentHp < this.previousHp) {
      this.triggerDamageFlash();
    }
    this.previousHp = currentHp;

    // Update HP text
    this.hpText.setText(`${Math.ceil(currentHp)} / ${Math.ceil(maxHp)}`);

    // Draw the health bar fill with gradient
    this.drawHealthFill(healthPercent, isLow);

    // Handle low health pulse
    if (isLow) {
      this.startLowHealthPulse();
    } else {
      this.stopLowHealthPulse();
    }
  }

  /**
   * Update every frame to track boss HP
   */
  update(): void {
    if (this.boss && this.visible) {
      this.updateHealth();

      // Auto-hide if boss is dead
      if (this.boss.isDead()) {
        this.hide('boss died');
        return;
      }

      // Auto-hide if player is out of range
      const player = this.scene.registry.get('player') as Phaser.GameObjects.Sprite | undefined;
      if (player && this.boss.activationRange) {
        const distance = Phaser.Math.Distance.Between(
          player.x, player.y,
          this.boss.x, this.boss.y
        );
        // Hide if player moves 1.5x activation range away
        const hideRange = this.boss.activationRange * 1.5;
        if (distance > hideRange) {
          console.log(`[BossHealthBar] Player out of range (${Math.round(distance)} > ${hideRange})`);
          this.hide('player out of range');
        }
      }
    }
  }

  /**
   * Check if currently showing
   */
  isShowing(): boolean {
    return this.visible && this.boss !== null;
  }

  /**
   * Get current boss (if any)
   */
  getCurrentBoss(): Boss | null {
    return this.boss;
  }
}
